import React, { useState, useEffect } from 'react';
import { contactAPI } from '../services/api';
import API_BASE_URL from '../config/api';
import Toast from './Toast';
import { useLang } from '../context/LanguageContext';
import '../styles/ContactCTA.css';

const ContactCTA = () => {
  const { nl } = useLang();
  const L = (obj, field) => (nl && obj?.[field + '_nl']) || obj?.[field] || '';
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [ctaData, setCtaData] = useState(null);
  const [formValues, setFormValues] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    generateCaptcha();
    fetchCTAData();
  }, []);

  const fetchCTAData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contactpage`);
      const data = await response.json();
      if (data.success) setCtaData(data.data);
    } catch (error) {
      console.error('Error fetching CTA data:', error);
    }
  };

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer('');
  };

  const validateName = (name) => /^[a-zA-Z\s'-]{2,}$/.test(name.trim());
  const validateEmail = (email) => /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const validate = (field, value) => {
    const newErrors = { ...errors };
    switch (field) {
      case 'firstName':
        if (!value.trim()) newErrors.firstName = nl ? 'Voornaam is verplicht' : 'First name is required';
        else if (!validateName(value)) newErrors.firstName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.firstName;
        break;
      case 'lastName':
        if (!value.trim()) newErrors.lastName = nl ? 'Achternaam is verplicht' : 'Last name is required';
        else if (!validateName(value)) newErrors.lastName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.lastName;
        break;
      case 'email':
        if (!value.trim()) newErrors.email = nl ? 'E-mail is verplicht' : 'Email is required';
        else if (!validateEmail(value)) newErrors.email = nl ? 'Ongeldig e-mailadres (bijv. naam@domein.com)' : 'Invalid email (e.g. name@domain.com)';
        else delete newErrors.email;
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
    if (touched[field]) validate(field, value);
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    validate(field, formValues[field]);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all
    setTouched({ firstName: true, lastName: true, email: true });
    const e1 = validate('firstName', formValues.firstName);
    const e2 = validate('lastName', formValues.lastName);
    const e3 = validate('email', formValues.email);
    const allErrors = { ...e1, ...e2, ...e3 };
    if (Object.keys(allErrors).length > 0) return;

    if (parseInt(captchaAnswer) !== captchaQuestion.answer) {
      alert(nl ? 'Ongeldige CAPTCHA' : 'Invalid CAPTCHA');
      generateCaptcha();
      return;
    }
    
    setSubmitting(true);
    try {
      await contactAPI.submit(formValues);
      setShowToast(true);
      setFormValues({ firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: '' });
      setErrors({});
      setTouched({});
      generateCaptcha();
    } catch (error) {
      alert(nl ? 'Fout bij verzenden. Probeer het opnieuw.' : 'Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const errorStyle = { color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' };

  return (
    <>
      {showToast && <Toast message={nl ? "Formulier succesvol verzonden!" : "Form submitted successfully!"} onClose={() => setShowToast(false)} />}
    <section className="contact-cta-section" style={{backgroundImage:'url(/images/CTA.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
      <div className="container">
        <div className="contact-header">
          <h2>{L(ctaData?.ctaSection, 'title') || (nl ? 'Klaar om te beginnen?' : 'Ready to get started?')}</h2>
          <p>{L(ctaData?.ctaSection, 'subtitle') || (nl ? 'Neem contact op met ons team om te bespreken hoe we uw bedrijf kunnen helpen transformeren' : 'Get in touch with our team to discuss how we can help transform your business')}</p>
        </div>
        
        <div className="contact-content" style={{ justifyContent: 'center' }}>
          <div className="contact-form-container" style={{ maxWidth: '800px', width: '100%', margin: '0 auto' }}>
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-row">
                <div>
                  <label htmlFor="footer-firstName">{nl ? 'Voornaam' : 'First Name'} *</label>
                  <input type="text" id="footer-firstName" name="firstName" placeholder={nl ? 'Voornaam' : 'First Name'} value={formValues.firstName} onChange={(e) => handleChange('firstName', e.target.value)} onBlur={() => handleBlur('firstName')} style={errors.firstName ? {borderColor: '#dc3545'} : {}} required />
                  {touched.firstName && errors.firstName && <span style={errorStyle}>{errors.firstName}</span>}
                </div>
                <div>
                  <label htmlFor="footer-lastName">{nl ? 'Achternaam' : 'Last Name'} *</label>
                  <input type="text" id="footer-lastName" name="lastName" placeholder={nl ? 'Achternaam' : 'Last Name'} value={formValues.lastName} onChange={(e) => handleChange('lastName', e.target.value)} onBlur={() => handleBlur('lastName')} style={errors.lastName ? {borderColor: '#dc3545'} : {}} required />
                  {touched.lastName && errors.lastName && <span style={errorStyle}>{errors.lastName}</span>}
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label htmlFor="footer-email">{nl ? 'E-mailadres' : 'Email Address'} *</label>
                  <input type="email" id="footer-email" name="email" placeholder={nl ? 'E-mailadres' : 'Email Address'} value={formValues.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={() => handleBlur('email')} style={errors.email ? {borderColor: '#dc3545'} : {}} required />
                  {touched.email && errors.email && <span style={errorStyle}>{errors.email}</span>}
                </div>
                <div>
                  <label htmlFor="footer-phone">{nl ? 'Telefoonnummer' : 'Phone Number'}</label>
                  <input type="tel" id="footer-phone" name="phone" placeholder={nl ? 'Telefoonnummer' : 'Phone Number'} value={formValues.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label htmlFor="footer-company">{nl ? 'Bedrijfsnaam' : 'Company Name'}</label>
                  <input type="text" id="footer-company" name="company" placeholder={nl ? 'Bedrijfsnaam' : 'Company Name'} value={formValues.company} onChange={(e) => handleChange('company', e.target.value)} />
                </div>
                <div>
                  <label htmlFor="footer-service">{nl ? 'Dienst interesse' : 'Service Interest'} *</label>
                  <select id="footer-service" name="service" value={formValues.service} onChange={(e) => handleChange('service', e.target.value)} required>
                    <option value="">{nl ? 'Selecteer een dienst' : 'Select a service'}</option>
                    <option value="ai">{nl ? 'AI-ontwikkeling' : 'AI Development'}</option>
                    <option value="blockchain">{nl ? 'Blockchain-ontwikkeling' : 'Blockchain Development'}</option>
                    <option value="cloud">{nl ? 'Cloud-oplossingen' : 'Cloud Solutions'}</option>
                    <option value="mobile">{nl ? 'Mobiele ontwikkeling' : 'Mobile Development'}</option>
                    <option value="web">{nl ? 'Webontwikkeling' : 'Web Development'}</option>
                    <option value="bot">{nl ? 'Bot-ontwikkeling' : 'Bot Development'}</option>
                    <option value="consulting">{nl ? 'Advies' : 'Consulting'}</option>
                    <option value="other">{nl ? 'Anders' : 'Other'}</option>
                  </select>
                </div>
              </div>
              <label htmlFor="footer-message">{nl ? 'Bericht' : 'Message'} *</label>
              <textarea id="footer-message" name="message" placeholder={nl ? 'Vertel ons over uw project' : 'Tell us about your project'} rows="3" value={formValues.message} onChange={(e) => handleChange('message', e.target.value)} required></textarea>
              <div className="captcha-row">
                <label htmlFor="footer-captcha">{nl ? 'Captcha! Wat is' : 'Captcha ! What is'} {captchaQuestion.num1} + {captchaQuestion.num2}?</label>
                <input 
                  type="number" 
                  id="footer-captcha" 
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder={nl ? 'Antwoord' : 'Answer'} 
                  required
                />
              </div>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {nl ? ' Verzenden...' : ' Sending...'}
                  </>
                ) : (
                  nl ? 'Bericht verzenden' : 'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default ContactCTA;
